export function buildCampaignPayload(data, options = {}) {
  const { basic, schedule, audience, sendRules, channels } = data;
  const { mode = "create", campaign = null } = options;

  // Sempre pega data/hora atual do Brasil
  function getBrazilDateTime() {
    const now = new Date();

    const formatter = new Intl.DateTimeFormat("sv-SE", {
      timeZone: "America/Sao_Paulo",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    const parts = formatter.formatToParts(now);
    const get = (type) => parts.find((p) => p.type === type)?.value;

    return {
      date: `${get("year")}-${get("month")}-${get("day")}`,
      time: `${get("hour")}:${get("minute")}`,
    };
  }

  //  Converte QUALQUER Date/string para data do Brasil (YYYY-MM-DD)
  function toBrazilDate(value) {
    if (!value) return null;

    // Se vier string "YYYY-MM-DD"
    const str = String(value);
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;

    // Se vier Date ou ISO
    const dateObj = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(dateObj.getTime())) return null;

    const formatter = new Intl.DateTimeFormat("sv-SE", {
      timeZone: "America/Sao_Paulo",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });

    return formatter.format(dateObj); // já vem YYYY-MM-DD no sv-SE
  }

  //  Converte QUALQUER Date/string para hora do Brasil (HH:mm)
  function toBrazilTime(value) {
    if (!value) return null;

    // Se vier string "HH:mm"
    const str = String(value);
    if (/^\d{2}:\d{2}$/.test(str)) return str;
    if (/^\d{2}:\d{2}:\d{2}$/.test(str)) return str.slice(0, 5);

    // Se vier Date ou ISO
    const dateObj = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(dateObj.getTime())) return null;

    const formatter = new Intl.DateTimeFormat("sv-SE", {
      timeZone: "America/Sao_Paulo",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    return formatter.format(dateObj); // HH:mm
  }

  const brazilNow = getBrazilDateTime();

  const isSingle = schedule?.type === "single";

  // =========================
  // SEND RULES (do wizard)
  // =========================
  const businessDaysOnly = Boolean(sendRules?.businessDaysOnly);

  const dueDateColumnId = sendRules?.dueDateColumnId ?? null;
  const reference = sendRules?.reference ?? null;

  const daysOffsetRaw = sendRules?.daysOffset ?? "";
  const isSameDay = reference === "sameDay";

  const daysOffset = isSameDay
    ? 0
    : daysOffsetRaw === "" || daysOffsetRaw === null
      ? null
      : Number(daysOffsetRaw);

  const ruleColumnId = sendRules?.ruleColumnId ?? null;
  const operator = sendRules?.operator ?? null;
  const valueRaw = sendRules?.value ?? "";

  const NO_VALUE_OPERATORS = ["is null", "is not null"];

  const operatorNeedsValue = operator && !NO_VALUE_OPERATORS.includes(operator);

  const value = operatorNeedsValue ? valueRaw?.trim() || null : null;

  // =========================
  // Flags
  // =========================
  const dynamic_date = Boolean(
    dueDateColumnId && reference && (isSameDay || daysOffset !== null),
  );

  const block_dynamic = Boolean(
    ruleColumnId && operator && (!operatorNeedsValue || value !== null),
  );

  // =========================
  //  Agendamento
  // =========================
  const startImmediately = Boolean(schedule?.sendImmediately);

  //   OS DOIS MODOS PASSAM PELO MESMO TRATAMENTO:
  const persistedStartDate = toBrazilDate(campaign?.start_date);
  const persistedExpirationDate = toBrazilDate(campaign?.expiration_date);
  const persistedStartTime = toBrazilTime(campaign?.start_time);

  const startDate = startImmediately
    ? mode === "edit"
      ? toBrazilDate(schedule?.startDate) || persistedStartDate || brazilNow.date
      : brazilNow.date
    : toBrazilDate(schedule?.startDate);

  const startTime = startImmediately
    ? mode === "edit"
      ? toBrazilTime(schedule?.startTime) || persistedStartTime || brazilNow.time
      : brazilNow.time
    : toBrazilTime(schedule?.startTime);

  const expirationDate = startImmediately
    ? mode === "edit"
      ? toBrazilDate(schedule?.endDate) || persistedExpirationDate || startDate
      : brazilNow.date
    : toBrazilDate(schedule?.endDate);

  const payload = {
    /* Básico */
    title: basic?.title || null,
    description: basic?.description || null,
    type: basic?.category || campaign?.type || campaign?.campaign_type || null,

    /* Agendamento */
    start_immediately: startImmediately,
    start_date: startDate,
    start_time: startTime,
    expiration_date: expirationDate,

    /* Público */
    list_id: audience?.audience_id || null,
    list_type: audience?.audience?.type || null,

    /* Regras */
    business_days: isSingle ? false : businessDaysOnly,
    dynamic_date,
    block_dynamic,

    key_date_row: dueDateColumnId,
    days_calculate: daysOffset,
    reference_moment: reference,

    key_block: ruleColumnId,
    when_block: operator,
    value_blocked: value,

    /* Canais */
    sms_id: channels?.sendSms ? channels?.smsChannelId : null,
    whatsapp_id: channels?.sendWhatsapp ? channels?.whatsappChannelId : null,
    email_id: channels?.sendEmail ? channels?.emailChannelId : null,

    model_sms_id: channels?.sendSms ? channels?.smsTemplateId || null : null,
    model_whatsapp_id: channels?.sendWhatsapp
      ? channels?.whatsappTemplateId || null
      : null,
    model_email_id: channels?.sendEmail
      ? channels?.emailTemplateId || null
      : null,

    visibility: campaign?.visibility ?? true,
  };

  if (mode === "edit") {
    payload.id_campaign = campaign?.id ?? campaign?.code ?? null;
  }

  return payload;
}
